import { useMemo } from 'react';
import { useTranslation, LOCALE_IDS } from '@parsina/framework/clinical/src/i18n/index';
import { lookupLocal, fillTemplate } from '../../lib/brandText';

// ─────────────────────────────────────────────────────────────────────────────
// Strings for «نوبت‌دهی آنلاین» (PM-0382) — the `settings.sections.booking.*` and
// `settings.onlineBooking.*` trees, and the BFF's `errors.bff.onlineBooking.*`
// sentences (parsina.bff.web docs/ERROR_CODES.md) so a refusal reads in the
// doctor's language instead of the server's Persian.
//
// They belong in the kit's settings.json and errors.json (the five JSON files
// beside this module are shaped as those files' fragments) and move there with
// the next kit release — the kit-lag rule, exactly as src/screens/MyPage/
// myPageStrings.js. Until then they live here, and `useOnlineBookingStrings` asks
// the kit FIRST: the day a released catalogue has a key, the kit's wording wins
// and this entry is dead, so the move is a deletion, never a behaviour change.
// Persian is authoritative; en, ar, fr and de carry every key (parity is tested).
// ─────────────────────────────────────────────────────────────────────────────

import fa from './onlineBookingStrings/fa.json';
import en from './onlineBookingStrings/en.json';
import ar from './onlineBookingStrings/ar.json';
import fr from './onlineBookingStrings/fr.json';
import de from './onlineBookingStrings/de.json';

const flatten = (obj, prefix = '', out = {}) => {
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === 'object' && !Array.isArray(v)) flatten(v, key, out); else out[key] = v;
  }
  return out;
};

const STRINGS = { fa: flatten(fa), en: flatten(en), ar: flatten(ar), fr: flatten(fr), de: flatten(de) };

/** Whether this module owns a key (so a caller can fall back to it). */
export const hasLocalOnlineBookingString = (key) => Object.prototype.hasOwnProperty.call(STRINGS.fa, key);

/**
 * Look a key up in the local table (English, then Persian, outside the Arabic
 * script — src/lib/brandText.js). Exported for tests.
 */
export function localOnlineBookingString(locale, key, vars) {
  const { value, from } = lookupLocal(STRINGS, locale, key);
  return value == null ? key : fillTemplate(value, vars, from);
}

/**
 * `{ t, has }` for this screen: the kit catalogue when it has the key, the local
 * table otherwise. `has` answers for both, so `userErrorMessage(err, strings)`
 * renders a BFF refusal from here too. Numbers are formatted by the caller
 * (`formatNumber` / `formatDigits`) before they are passed in.
 */
export function useOnlineBookingStrings() {
  const { t, has, locale } = useTranslation();
  return useMemo(() => ({
    t: (key, vars) => (has(key) || !hasLocalOnlineBookingString(key)
      ? t(key, vars)
      : localOnlineBookingString(locale, key, vars)),
    has: (key) => has(key) || hasLocalOnlineBookingString(key),
  }), [t, has, locale]);
}

// Every language the kit ships (a copy: a test may sort it), so a missing table
// here is a test failure rather than a quiet fallback.
export const ONLINE_BOOKING_STRING_LOCALES = [...LOCALE_IDS];
export const ONLINE_BOOKING_STRING_KEYS = Object.keys(STRINGS.fa);
export const onlineBookingStringTable = (locale) => STRINGS[locale];
